import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

const TOAST_CONFIG = {
  success: { icon: '\u2705', bg: '#28a745' },
  error: { icon: '\u274c', bg: '#dc3545' },
  warning: { icon: '\u26a0\ufe0f', bg: '#e67e22' },
  info: { icon: '\u2139\ufe0f', bg: '#17a2b8' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t =>
      t.id === id ? { ...t, exiting: true } : t
    ));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, exiting: false }]);

    if (duration > 0) {
      timerRef.current[id] = setTimeout(() => {
        removeToast(id);
        delete timerRef.current[id];
      }, duration);
    }

    return id;
  }, [removeToast]);

  useEffect(() => {
    return () => {
      Object.values(timerRef.current).forEach(clearTimeout);
    };
  }, []);

  const toast = useCallback({
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur ?? 5000),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
  }, [addToast]);

  // Fix: useCallback can't wrap an object directly - use useMemo pattern
  const toastApi = React.useMemo(() => ({
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur ?? 5000),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      <div style={{
        position: 'fixed',
        bottom: 'calc(var(--nav-height, 64px) + 1rem)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 'var(--z-toast, 9000)',
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: '0.5rem',
        alignItems: 'center',
        pointerEvents: 'none',
        width: '90%',
        maxWidth: '400px',
      }}>
        {toasts.map(t => {
          const config = TOAST_CONFIG[t.type] || TOAST_CONFIG.info;
          return (
            <div
              key={t.id}
              style={{
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: config.bg,
                color: 'white',
                borderRadius: 'var(--radius-xl, 1rem)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                fontSize: '0.9rem',
                fontWeight: 500,
                animation: t.exiting
                  ? 'slideDown 0.3s ease forwards'
                  : 'slideUp 0.3s ease forwards',
                cursor: 'pointer',
                width: '100%',
                justifyContent: 'center',
              }}
              onClick={() => removeToast(t.id)}
            >
              <span>{config.icon}</span>
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
